/** Customer analytics (`acp.customers|abc|rfm|ret|churn|lr`). `acpCustomersEn` is typed against the Polish shape. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const acpCustomersPl = {
  customers: {
    title: 'Analiza klientów',
    subtitle: 'Kto kupuje, kto odchodzi i komu warto się przyjrzeć — ABC, RFM, retencja, ryzyko odejścia.',
    tab: {
      abc: 'ABC / Pareto',
      rfm: 'RFM',
      retention: 'Retencja',
      churn: 'Ryzyko odejścia',
      lost: 'Utraceni i odzyskani',
    },
  },
  abc: {
    kpiSection: 'Wskaźniki ABC',
    classesSection: 'Klasy ABC',
    classTitle: 'Klasa {c}',
    range: {
      a: 'do {a}% skumulowanej sprzedaży',
      b: 'od {a}% do {b}% sprzedaży',
      c: 'ostatnie {b}% → 100% sprzedaży',
    },
    kpi: {
      sales: 'Sprzedaż w okresie',
      customers: 'Klienci ze sprzedażą',
      customersHint: 'z dodatnią sprzedażą w okresie',
      top10: 'Udział TOP 10',
      top10Hint: '10 największych klientów',
      pareto: 'Najlepsze 20% klientów',
      paretoHint: 'dają tyle sprzedaży · połowę robi {n} klientów',
      hhi: 'Koncentracja (HHI)',
    },
    hhi: {
      low: 'niska koncentracja (< 1 500)',
      moderate: 'umiarkowana (1 500–2 500)',
      high: 'wysoka koncentracja (> 2 500)',
    },
    tile: {
      customers: 'Klienci',
      sales: 'Sprzedaż',
      avg: 'Śr. / klient',
      dCustomers: 'Klienci vs poprz.',
      dShare: 'Udział vs poprz.',
    },
    pareto: {
      title: 'Krzywa Pareto — najwięksi klienci',
      subtitle:
        '{n} największych z {total} klientów · słupki = sprzedaż, linia = skumulowany udział · kliknij słupek, by otworzyć klienta',
      class: 'klasa',
      cumulative: 'Skumulowany udział',
      ofSales: 'sprzedaży',
      legendA: 'Klasa A',
      legendB: 'Klasa B',
      legendC: 'Klasa C',
    },
    conc: {
      title: 'Koncentracja sprzedaży',
      subtitle: 'Jaki odsetek sprzedaży robi dany odsetek klientów',
      curve: 'Krzywa koncentracji',
      equal: 'Równy podział',
      point: '20% klientów',
      customers: 'klientów',
      sales: 'sprzedaży',
    },
    table: {
      title: 'Ranking klientów',
      subtitle: '{n} klientów · kliknij wiersz, by otworzyć kartę klienta',
      class: 'Klasa',
      share: 'Udział',
      cumulative: 'Skumul.',
      new: 'nowy',
      moved: 'Klasa {from} → {to}',
    },
  },
  rfm: {
    segmentsSection: 'Segmenty RFM',
    tile: { sales: 'Sprzedaż 12 m', avgOrder: 'Śr. zam.' },
    seg: {
      CHAMPIONS: 'Champions',
      LOYAL: 'Lojalni',
      POTENTIAL: 'Potencjalnie lojalni',
      NEW: 'Nowi',
      AT_RISK: 'Zagrożeni',
      HIBERNATING: 'Hibernujący',
      LOST: 'Utraceni',
    },
    segDesc: {
      CHAMPIONS: 'Kupują często, niedawno i za dużo — R≥4, F≥4, M≥4',
      LOYAL: 'Regularni, niedawni kupujący — R≥3, F≥3',
      POTENTIAL: 'Niedawni, ale kupują rzadziej lub za mało — R≥3, F≤2',
      NEW: 'Pierwsze zamówienie w ostatnich 90 dniach',
      AT_RISK: 'Dawniej dobrzy, dawno nie kupowali — R≤2, F≥3 lub M≥3',
      HIBERNATING: 'Rzadko i dawno, niska wartość — R≤2, F≤2, M≤2',
      LOST: 'Brak zakupu od ponad {days} dni',
    },
    share: {
      title: 'Klienci a sprzedaż wg segmentów',
      subtitle: 'Udział segmentu w liczbie klientów i w sprzedaży 12 mies.',
      customers: 'Udział w liczbie klientów',
      sales: 'Udział w sprzedaży (12 mies.)',
      note: 'Analizowanych klientów: {n} (kupili co najmniej raz do daty odniesienia).',
    },
    grid: {
      title: 'Mapa świeżości × częstotliwości',
      subtitle: 'Liczba klientów w każdej parze ocen R (kiedy kupił) i F (jak często)',
      customers: 'klientów',
      recency: 'ostatni zakup',
      frequency: 'zamówień',
      note: 'R5 = kupował niedawno, F5 = kupuje najczęściej. Progi wynikają z rozkładu portfela.',
    },
    list: {
      title: 'Klienci wg RFM',
      titleSegment: 'Segment: {seg}',
      subtitle: '{n} klientów · kliknij wiersz, by otworzyć kartę klienta',
      clear: 'Wszyscy',
      segment: 'Segment',
      recency: 'Ostatni zakup',
      frequency: 'Zamówień',
      monetary: 'Sprzedaż 12 mies.',
      avgOrder: 'Śr. zamówienie',
      lastOrder: 'Ostatnie zam.',
    },
  },
};

export type AcpCustomers = typeof acpCustomersPl;

export const acpCustomersEn: Shape<AcpCustomers> = {
  customers: {
    title: 'Customer analytics',
    subtitle: 'Who buys, who is leaving and who deserves a closer look — ABC, RFM, retention, churn risk.',
    tab: {
      abc: 'ABC / Pareto',
      rfm: 'RFM',
      retention: 'Retention',
      churn: 'Churn risk',
      lost: 'Lost & recovered',
    },
  },
  abc: {
    kpiSection: 'ABC indicators',
    classesSection: 'ABC classes',
    classTitle: 'Class {c}',
    range: {
      a: 'up to {a}% of cumulative sales',
      b: '{a}% to {b}% of sales',
      c: 'last {b}% → 100% of sales',
    },
    kpi: {
      sales: 'Sales in period',
      customers: 'Customers with sales',
      customersHint: 'with positive sales in the period',
      top10: 'Top 10 share',
      top10Hint: 'the 10 biggest customers',
      pareto: 'Best 20% of customers',
      paretoHint: 'generate this share · half comes from {n} customers',
      hhi: 'Concentration (HHI)',
    },
    hhi: {
      low: 'low concentration (< 1,500)',
      moderate: 'moderate (1,500–2,500)',
      high: 'high concentration (> 2,500)',
    },
    tile: {
      customers: 'Customers',
      sales: 'Sales',
      avg: 'Avg / customer',
      dCustomers: 'Customers vs prev.',
      dShare: 'Share vs prev.',
    },
    pareto: {
      title: 'Pareto curve — biggest customers',
      subtitle:
        '{n} biggest of {total} customers · bars = sales, line = cumulative share · click a bar to open the customer',
      class: 'class',
      cumulative: 'Cumulative share',
      ofSales: 'of sales',
      legendA: 'Class A',
      legendB: 'Class B',
      legendC: 'Class C',
    },
    conc: {
      title: 'Sales concentration',
      subtitle: 'What share of sales comes from what share of customers',
      curve: 'Concentration curve',
      equal: 'Equal split',
      point: '20% of customers',
      customers: 'of customers',
      sales: 'of sales',
    },
    table: {
      title: 'Customer ranking',
      subtitle: '{n} customers · click a row to open the customer card',
      class: 'Class',
      share: 'Share',
      cumulative: 'Cumul.',
      new: 'new',
      moved: 'Class {from} → {to}',
    },
  },
  rfm: {
    segmentsSection: 'RFM segments',
    tile: { sales: 'Sales 12 mo', avgOrder: 'Avg order' },
    seg: {
      CHAMPIONS: 'Champions',
      LOYAL: 'Loyal',
      POTENTIAL: 'Potential loyalists',
      NEW: 'New',
      AT_RISK: 'At risk',
      HIBERNATING: 'Hibernating',
      LOST: 'Lost',
    },
    segDesc: {
      CHAMPIONS: 'Buy often, recently and a lot — R≥4, F≥4, M≥4',
      LOYAL: 'Regular, recent buyers — R≥3, F≥3',
      POTENTIAL: 'Recent, but buy less often or too little — R≥3, F≤2',
      NEW: 'First order within the last 90 days',
      AT_RISK: 'Used to be good, have not bought for a while — R≤2, F≥3 or M≥3',
      HIBERNATING: 'Rare and long ago, low value — R≤2, F≤2, M≤2',
      LOST: 'No purchase for over {days} days',
    },
    share: {
      title: 'Customers vs sales by segment',
      subtitle: 'Segment share of the customer count and of 12-month sales',
      customers: 'Share of customers',
      sales: 'Share of sales (12 mo.)',
      note: 'Customers analysed: {n} (bought at least once up to the reference date).',
    },
    grid: {
      title: 'Recency × frequency map',
      subtitle: 'Customers per pair of R (when they bought) and F (how often) scores',
      customers: 'customers',
      recency: 'last purchase',
      frequency: 'orders',
      note: 'R5 = bought recently, F5 = buys most often. Thresholds come from the portfolio distribution.',
    },
    list: {
      title: 'Customers by RFM',
      titleSegment: 'Segment: {seg}',
      subtitle: '{n} customers · click a row to open the customer card',
      clear: 'Everyone',
      segment: 'Segment',
      recency: 'Last purchase',
      frequency: 'Orders',
      monetary: 'Sales 12 mo.',
      avgOrder: 'Avg order',
      lastOrder: 'Last order',
    },
  },
};
